/**
 * The player hull — a sleek arrowhead. Bank (roll) is presentation-only (FS-05 R16). During i-frames
 * the hull switches to a blink/stipple (shape, not hue — Design Bible §12).
 */
import * as THREE from 'three';
import { SHIP_VISUAL_RADIUS } from '../core/constants';
import { PALETTE } from './palette';

export class Hull {
  readonly group = new THREE.Group();
  private body: THREE.Mesh;
  private glow: THREE.Mesh;
  private engine: THREE.Mesh;
  private blink = 0;

  constructor() {
    // Arrowhead pointing into the screen (−Z).
    const bodyGeo = new THREE.ConeGeometry(SHIP_VISUAL_RADIUS, SHIP_VISUAL_RADIUS * 2.4, 4);
    bodyGeo.rotateX(-Math.PI / 2);
    const bodyMat = new THREE.MeshBasicMaterial({ color: PALETTE.hull });
    this.body = new THREE.Mesh(bodyGeo, bodyMat);
    this.group.add(this.body);

    const wireGeo = new THREE.EdgesGeometry(bodyGeo);
    const wire = new THREE.LineSegments(wireGeo, new THREE.LineBasicMaterial({ color: 0xffffff }));
    this.group.add(wire);

    const glowGeo = new THREE.SphereGeometry(SHIP_VISUAL_RADIUS * 1.7, 12, 12);
    const glowMat = new THREE.MeshBasicMaterial({
      color: PALETTE.primary,
      transparent: true,
      opacity: 0.18,
      side: THREE.BackSide,
    });
    this.glow = new THREE.Mesh(glowGeo, glowMat);
    this.group.add(this.glow);

    const engGeo = new THREE.CircleGeometry(SHIP_VISUAL_RADIUS * 0.55, 16);
    const engMat = new THREE.MeshBasicMaterial({
      color: 0xff5da2,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
    });
    this.engine = new THREE.Mesh(engGeo, engMat);
    this.engine.position.z = SHIP_VISUAL_RADIUS * 1.3;
    this.group.add(this.engine);
  }

  update(x: number, y: number, bank: number, iframes: number, overdrive: boolean, dt: number): void {
    this.group.position.set(x, y, 0);
    this.group.rotation.z = bank;

    // Engine pulse.
    const pulse = 0.7 + 0.3 * Math.sin(performance.now() * 0.02);
    (this.engine.material as THREE.MeshBasicMaterial).opacity = 0.6 * pulse;
    this.engine.scale.setScalar(0.85 + 0.25 * pulse);

    // i-frame blink (2.5 Hz, below the 3 Hz photosensitivity cap).
    if (iframes > 0) {
      this.blink += dt * 2.5 * Math.PI * 2;
      const on = Math.sin(this.blink) > 0;
      this.group.visible = on;
    } else {
      this.group.visible = true;
      this.blink = 0;
    }

    const bodyMat = this.body.material as THREE.MeshBasicMaterial;
    const glowMat = this.glow.material as THREE.MeshBasicMaterial;
    if (overdrive) {
      bodyMat.color.copy(PALETTE.overdrive);
      glowMat.color.copy(PALETTE.overdrive);
      glowMat.opacity = 0.4;
    } else {
      bodyMat.color.copy(PALETTE.hull);
      glowMat.color.copy(PALETTE.primary);
      glowMat.opacity = 0.18;
    }
  }
}
